import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'openblock-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    openExtensionLibrary
} from '../reducers/modals';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';

class GUI extends React.Component {
    componentDidMount () {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
    }
    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId && this.props.projectId !== null) {
            this.props.onUpdateProjectId(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        if (this.props.isRealtimeMode !== true) {
            this.props.onActivateBlocksTab();
        }
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onActivateBlocksTab,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onActivateBlocksTab: PropTypes.func,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    telemetryModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-01 — flag forwarded to GUIComponent for modal mount.
    newProjectModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-02 — flag forwarded to GUIComponent for modal mount.
    openProjectModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-03 — flag forwarded to GUIComponent for modal mount.
    saveProjectModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-04 — flag forwarded to GUIComponent for modal mount.
    shareProjectModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-05 — flag forwarded to GUIComponent for modal mount.
    sharedProjectsModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 3 PRJ-06 — flag forwarded to GUIComponent for tutorial modal mount.
    tutorialModalVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 4 ROB-02..04 — flag forwarded to GUIComponent for robot block info modal mount.
    robotBlockInfoVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 5 SMH-02 — flag forwarded to GUIComponent for SmartHome dashboard modal mount.
    smartHomeDashboardVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 6 EXT-01..04 — flags forwarded to GUIComponent for "Khối Mở rộng" modal mounts.
    deviceDeclarationVisible: PropTypes.bool,
    blockBuilderVisible: PropTypes.bool,
    ledDesignerVisible: PropTypes.bool,
    publishWizardVisible: PropTypes.bool,
    // MSTEM-ADDED UC-43 / UC-47 — "Quản lý ... mở rộng" manager modal flags.
    extDeviceManagerVisible: PropTypes.bool,
    extBlockManagerVisible: PropTypes.bool,
    aiPanelVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRealtimeMode: PropTypes.bool
};

GUI.defaultProps = {
    isScratchDesktop: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        uploadProgressVisible: state.scratchGui.modals.uploadProgress,
        updateModalVisible: state.scratchGui.modals.updateModal,
        themeManagerVisible: state.scratchGui.modals.themeManager,
        robotManagerVisible: state.scratchGui.modals.robotManager,
        kitManagerVisible: state.scratchGui.modals.kitManager,
        firmwareWizardVisible: state.scratchGui.modals.firmwareWizard,
        // MSTEM-ADDED Phase 3 PRJ-01 — New project modal flag (alphabetical: NewProject).
        newProjectModalVisible: state.scratchGui.modals.newProjectModal,
        // MSTEM-ADDED Phase 3 PRJ-02 — Open project modal flag (alphabetical: OpenProject).
        openProjectModalVisible: state.scratchGui.modals.openProjectModal,
        // MSTEM-ADDED Phase 3 PRJ-03 — Save project modal flag (alphabetical: SaveProject).
        saveProjectModalVisible: state.scratchGui.modals.saveProjectModal,
        // MSTEM-ADDED Phase 3 PRJ-04 — Share project modal flag (alphabetical: Sa < Sh).
        shareProjectModalVisible: state.scratchGui.modals.shareProjectModal,
        // MSTEM-ADDED Phase 3 PRJ-05 — Shared projects modal flag (alphabetical: Share < Shared).
        sharedProjectsModalVisible: state.scratchGui.modals.sharedProjectsModal,
        // MSTEM-ADDED Phase 3 PRJ-06 — Tutorial modal flag (alphabetical: Shared < Tutorial).
        tutorialModalVisible: state.scratchGui.modals.tutorialModal,
        // MSTEM-ADDED Phase 4 ROB-02..04 — Robot block info modal flag (DR-05).
        robotBlockInfoVisible: state.scratchGui.modals.robotBlockInfo,
        // MSTEM-ADDED Phase 5 SMH-02 — SmartHome dashboard modal flag (DR-04).
        smartHomeDashboardVisible: state.scratchGui.modals.smartHomeDashboard,
        // MSTEM-ADDED Phase 6 EXT-01..04 — "Khối Mở rộng" authoring modal flags (DR-05).
        deviceDeclarationVisible: state.scratchGui.modals.deviceDeclarationModal,
        blockBuilderVisible: state.scratchGui.modals.blockBuilderModal,
        ledDesignerVisible: state.scratchGui.modals.ledMatrixDesignerModal,
        publishWizardVisible: state.scratchGui.modals.publishWizardModal,
        // MSTEM-ADDED UC-43 / UC-47 — "Quản lý ... mở rộng" manager modal flags.
        extDeviceManagerVisible: state.scratchGui.modals.extDeviceManagerModal,
        extBlockManagerVisible: state.scratchGui.modals.extBlockManagerModal,
        aiPanelVisible: state.scratchGui.modals.aiPanel,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,
        isRealtimeMode: state.scratchGui.programMode.isRealtimeMode
    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateBlocksTab: () => dispatch(activateTab(BLOCKS_TAB_INDEX)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal())
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
